import React from 'react';
import { AncientOne, ZharSpecialData, BokrugSpecialData } from '../api';
import { normalizeCardUri } from './Card';
import { Monsters } from './Monsters';
import { ScaledArtwork } from './ScaledArtwork';

import './AncientOneCard.css';

interface AncientOneSpecialDataProps {
  ancientOne?: AncientOne;
  isFinalBattle?: boolean;
  detailsOnly?: boolean;
}

const renderSpecialData = ({ancientOne, isFinalBattle, detailsOnly = false}: AncientOneSpecialDataProps) => {
  if (!ancientOne || ancientOne.specialData === undefined || ancientOne?.specialData === null) {
    return null;
  }

  if (ancientOne.name === 'Zhar') {
    const data = ancientOne.specialData as ZharSpecialData;
    return (
      <div className='ancient-one-special-text'>
        {!detailsOnly && <><h3>Zhar</h3>
        <div>Combat rating: {ancientOne.modifier}</div></>}
        <div>Zhar: {data.firstDefeatOccurred ? 'Second form' : 'First form'}</div>
        {!detailsOnly && <>
        <div>{isFinalBattle ? (data.firstDefeatOccurred ? 'Physical Immunity' : 'Magical Immunity') : 'Defenses: Special'}</div>
        <div>Doom: {ancientOne.doomTrack}/{ancientOne.maximumDoomTrack}</div>
        <p>{ancientOne.worshippersDescription}</p>
        <p>{ancientOne.powerDescription}</p>
        {isFinalBattle && <p>{ancientOne.attackDescription}</p>}
        </>}
      </div>
    );
  }

  if (ancientOne.name === "Shudde M'ell") {
    // Special data is a list of locations.
    return (
      <div className='ancient-one-special-text'>Rubble tokens left: {(ancientOne.specialData as any[]).length}</div>
    );
  }

  if (ancientOne.name === "Abhoth" || ancientOne.name === "Glaaki") {
    // Special data is a list of Monsters.
    if (detailsOnly && ancientOne.specialData.length === 0) return null;
    return (
      <Monsters monsters={ancientOne.specialData} tooltipPosition='left' />
    );
  }

  if (ancientOne.name === 'Nyogtha') {
    if (detailsOnly && isFinalBattle) return null;
    return (
      <div className='ancient-one-special-text'>
        <Monsters monsters={ancientOne.specialData} tooltipPosition='left' />
      </div>
    );
  }

  if (ancientOne.name === 'Bokrug') {
    const data = ancientOne.specialData as BokrugSpecialData;
    return (
      <div className='ancient-one-special-text'>
        <Monsters monsters={data.beingsOfIb} tooltipPosition='left' />
      </div>
    );
  }

  return null;
}

export const AncientOneSpecialData: React.FC<AncientOneSpecialDataProps> = (props) => {
  const content = renderSpecialData(props);
  if (!content || !props.detailsOnly) return content;
  return <section className="ancient-one-digital-special ancient-one-digital-block" aria-label="Special details">
    <h4>Special details</h4>
    {content}
  </section>;
};

export interface AncientOneCardProps {
  ancientOne?: AncientOne;
  isFinalBattle?: boolean;
  worshippersDescription?: string | null;
  powerDescription?: string | null;
  attackDescription?: string | null;
  startOfBattleDescription?: string | null;
  ancientOneDamageDealt?: number;
  ancientOneDamageThreshold?: number | null;
  responsive?: boolean;
}

export const AncientOneCard: React.FC<AncientOneCardProps> = ({
  ancientOne,
  isFinalBattle,
  worshippersDescription,
  powerDescription,
  attackDescription,
  ancientOneDamageDealt,
  responsive = false
}) => {
  if (!ancientOne) {
    return <p className="ancient-one-placeholder">Ancient One has not been revealed yet.</p>;
  }
  const ancientOneName = ancientOne.name;
  const ancientOneImage = `/AncientOnes/${normalizeCardUri(ancientOne.name)}.png`;
  if (!ancientOneName || !ancientOneImage) {
    return <p className="ancient-one-placeholder">Ancient One has not been revealed yet.</p>;
  }
  const maximumDoomTrack = ancientOne.maximumDoomTrack;
  const doomTrack = ancientOne.doomTrack;

  // const hasDetails = Boolean(worshippersDescription || powerDescription || attackDescription);
  // TODO: add layout, change input to AncientOne.
  const hasDetails = false;

  const elements = [];
  for (let i = 0; i < (ancientOneDamageDealt || 0); i++) {
    elements.push(
      <span key={`heart-${i}`} className="heart" />
    );
  }

  const cardFace = (
      <div className="ancient-one-card">
        <img loading="lazy"
          key={ancientOneName}
          src={ancientOneImage}
          alt={ancientOneName}
          className="ancient-one-portrait"
          onError={event => { if (ancientOne.name === 'Zhar' || ancientOne.name === 'Bokrug' || ancientOne.name === 'Ghatanothoa' || ancientOne.name === 'Nyogtha' || ancientOne.name === 'Rhan-Tegoth') event.currentTarget.style.display = 'none'; }}
        />
        <div className="ancient-one-damage">{elements}</div>
        {!!doomTrack && !!maximumDoomTrack && (
          <div className='doom-track-eyes'>
            {Array.from({length: Math.min(doomTrack, maximumDoomTrack)}, (_, i) => i + 1).map((i) => (
              <div key={i} className={`doom-track-eye doom-track-eye-${i}-${maximumDoomTrack}`} />
            ))}
          </div>
        )}
        {hasDetails && (
          <div className="ancient-one-details">
            {worshippersDescription && (
              <div className="ancient-one-details-section">
                <div className="ancient-one-details-label">Worshippers</div>
                <div className="ancient-one-details-text">{worshippersDescription}</div>
              </div>
            )}
            {powerDescription && (
              <div className="ancient-one-details-section">
                <div className="ancient-one-details-label">Power</div>
                <div className="ancient-one-details-text">{powerDescription}</div>
              </div>
            )}
            {attackDescription && (
              <div className="ancient-one-details-section">
                <div className="ancient-one-details-label">Attack</div>
                <div className="ancient-one-details-text">{attackDescription}</div>
              </div>
            )}
          </div>
        )}
      </div>
  );
  return (
    <div className={`ancient-one-card-layout${responsive ? ' ancient-one-card-responsive' : ''}`}>
      {responsive ? <ScaledArtwork width={358} height={500}>{cardFace}</ScaledArtwork> : cardFace}
      <div className='ancient-one-special-data'>
        {doomTrack > maximumDoomTrack && <div className='ancient-one-special-text'>Doom: {doomTrack}/{maximumDoomTrack}</div>}
        <AncientOneSpecialData ancientOne={ancientOne} isFinalBattle={isFinalBattle} />
      </div>
    </div>
  );
};
